import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FirebaseResponseCode,
  confirmNewPassword,
  sendPasswordReset,
  validatePasswordResetLink,
} from "../firebase";
import { ROUTES } from "../routes";
import PasswordSet from "../components/PasswordSet";
import { sanitizeAndValidateEmail } from "../utils/validation";

export default function ForgottenPassword() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if this is a password reset with code (from email link)
  const queryParams = new URLSearchParams(location.search);
  const oobCode = queryParams.get("oobCode");
  const isResetMode = !!oobCode;

  // Handle sending password reset email
  const handleSendResetEmail = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    // Validate email
    const emailValidation = sanitizeAndValidateEmail(email);
    if (!emailValidation.isValid) {
      setMessage(emailValidation.error || "Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      const response = await sendPasswordReset(emailValidation.sanitized);
      
      if (response.code === FirebaseResponseCode.OK) {
        setEmailSent(true);
        setMessage("Password reset email sent! Please check your inbox and spam folder.");
      } else {
        setMessage(response.message);
      }
    } catch (error) {
      console.error("Password reset error:", error);
      setMessage("Failed to send reset email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle setting new password (when coming from email link)
  const handlePasswordReset = async (password: string) => {
    if (!oobCode) {
      setMessage("Invalid or missing password reset code.");
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await confirmNewPassword(oobCode, password);
      setMessage(response.message);

      if (response.code === FirebaseResponseCode.OK) {
        setMessage("Password reset successful! Redirecting to login...");
        setTimeout(() => {
          navigate(ROUTES.LOGIN);
        }, 2000);
      }
    } catch (error) {
      setMessage("Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Validate reset link when in reset mode
  useEffect(() => {
    const validateLink = async () => {
      if (!oobCode) return;
      
      setIsLoading(true);
      const response = await validatePasswordResetLink(oobCode);
      
      if (response.code !== FirebaseResponseCode.OK) {
        setMessage("Invalid or expired reset link. Please request a new one.");
      }
      setIsLoading(false);
    };

    if (isResetMode) {
      validateLink();
    }
  }, [isResetMode, oobCode]);

  return (
    <div className="login-container">
      <h2>{isResetMode ? "Reset Your Password" : "Forgot Password"}</h2>
      
      {message && (
        <div style={{ 
          color: message.includes("successful") || message.includes("sent") ? 'green' : 'red',
          margin: '10px 0',
          padding: '10px',
          border: `1px solid ${message.includes("successful") || message.includes("sent") ? 'green' : 'red'}`,
          borderRadius: '4px',
          backgroundColor: message.includes("successful") || message.includes("sent") ? '#d4edda' : '#f8d7da'
        }}>
          {message}
        </div>
      )}

      {isResetMode ? (
        // Password reset mode (coming from email link)
        <div>
          <p>Enter your new password below:</p>
          <PasswordSet 
            confirmButtonText={isLoading ? "Resetting..." : "Reset Password"} 
            onConfirm={handlePasswordReset}
          />
        </div>
      ) : emailSent ? (
        // Email sent confirmation
        <div style={{ textAlign: 'center' }}>
          <p>✅ Reset email sent to <strong>{email}</strong></p>
          <p>Please check your inbox and spam folder for the reset link.</p>
          <button 
            onClick={() => {
              setEmailSent(false);
              setEmail("");
              setMessage("");
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            Send Another Reset Email
          </button>
          <br />
          <button 
            onClick={() => navigate(ROUTES.LOGIN)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            Back to Login
          </button>
        </div>
      ) : (
        // Email input form
        <div>
          <p>Enter your email address and we'll send you a link to reset your password.</p>
          <form className="login-form" onSubmit={handleSendResetEmail}>
            <div className="input-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                maxLength={254}
                autoComplete="email"
                disabled={isLoading}
              />
            </div>
            
            <button 
              type="submit" 
              disabled={isLoading}
              style={{
                padding: '12px 24px',
                backgroundColor: isLoading ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                width: '100%',
                marginBottom: '10px'
              }}
            >
              {isLoading ? "Sending..." : "Send Reset Email"}
            </button>
            
            <button 
              type="button"
              onClick={() => navigate(ROUTES.LOGIN)}
              style={{
                padding: '10px 20px',
                backgroundColor: 'transparent',
                color: '#007bff',
                border: '1px solid #007bff',
                borderRadius: '4px',
                cursor: 'pointer',
                width: '100%'
              }}
            >
              Back to Login
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
